import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Phone, User, History as HistoryIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PatientLookup = () => {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [activeField, setActiveField] = useState(null); // 'phone' | 'name'
  const [loading, setLoading] = useState(true);
  const suggestRef = useRef(null);

  useEffect(() => {
    const fetchPatients = async () => {
      try {
        const { data } = await axios.get(`${API}/patients`);
        setPatients(data);
      } catch (e) {
        console.error('Error fetching patients:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchPatients();
  }, []);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (suggestRef.current && !suggestRef.current.contains(e.target)) {
        setActiveField(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const suggestions = useMemo(() => {
    const q = (activeField === 'name' ? name : phone).trim().toLowerCase();
    if (!q) return patients.slice(0, 8);
    return patients
      .filter((p) => !p.archived)
      .filter((p) =>
        (p.name || '').toLowerCase().includes(q) || (p.phone || '').toLowerCase().includes(q)
      )
      .slice(0, 12);
  }, [patients, phone, name, activeField]);

  const goToHistory = (patient) => {
    navigate(`/patients/${patient.id}/history`);
  };

  const exactPhoneMatch = useMemo(() => {
    const q = phone.trim();
    if (!q) return null;
    return patients.find((p) => (p.phone || '') === q && !p.archived) || null;
  }, [patients, phone]);

  const onSubmit = (e) => {
    e.preventDefault();
    // Priority: exact phone match → first suggestion → nothing
    if (exactPhoneMatch) return goToHistory(exactPhoneMatch);
    if (suggestions.length > 0) return goToHistory(suggestions[0]);
    alert('No matching patient found. Add the patient from the Patients page first.');
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-4xl font-bold" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }} data-testid="patient-lookup-title">
          Patient Lookup
        </h1>
        <p className="mt-2" style={{ color: '#6B7280' }}>
          Find a patient by phone number or name to view their full medical timeline
        </p>
      </div>

      <div className="card p-6 mb-8" ref={suggestRef}>
        <form onSubmit={onSubmit} className="relative">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="flex items-center gap-2">
                <Phone size={14} style={{ color: '#990099' }} />
                Phone number
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => { setPhone(e.target.value); setActiveField('phone'); }}
                onFocus={() => setActiveField('phone')}
                placeholder="e.g. 9876543210"
                autoComplete="off"
                data-testid="lookup-phone-input"
                autoFocus
              />
            </div>
            <div>
              <label className="flex items-center gap-2">
                <User size={14} style={{ color: '#990099' }} />
                Name (optional)
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => { setName(e.target.value); setActiveField('name'); }}
                onFocus={() => setActiveField('name')}
                placeholder="Patient name"
                autoComplete="off"
                data-testid="lookup-name-input"
              />
            </div>
          </div>

          {activeField && (
            <ul
              className="absolute z-20 mt-1 w-full bg-white rounded-lg shadow-lg overflow-hidden"
              style={{ border: '1px solid #E5E0D8', maxHeight: 320, overflowY: 'auto', top: '100%' }}
              data-testid="lookup-suggestions"
            >
              {loading ? (
                <li className="px-3 py-3 text-sm" style={{ color: '#6B7280' }}>Loading patients...</li>
              ) : suggestions.length === 0 ? (
                <li className="px-3 py-3 text-sm" style={{ color: '#6B7280' }} data-testid="lookup-no-match">
                  No patient matches — they may need to be added from Billing or Patients page.
                </li>
              ) : (
                suggestions.map((p) => (
                  <li
                    key={p.id}
                    onMouseDown={(e) => { e.preventDefault(); goToHistory(p); }}
                    className="px-4 py-3 cursor-pointer hover:bg-gray-50 flex items-center justify-between gap-3 border-b last:border-b-0"
                    style={{ borderColor: '#F3F4F6' }}
                    data-testid={`lookup-suggestion-${p.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F5E6F5' }}>
                        <User size={18} style={{ color: '#990099' }} />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium truncate" style={{ color: '#2D3A2A' }}>
                          {p.name || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>No name</span>}
                        </p>
                        <p className="text-xs truncate" style={{ color: '#6B7280' }}>{p.phone}{p.age ? ` · ${p.age} yr` : ''}{p.gender ? ` · ${p.gender}` : ''}</p>
                      </div>
                    </div>
                    <HistoryIcon size={18} style={{ color: '#990099', flexShrink: 0 }} />
                  </li>
                ))
              )}
            </ul>
          )}

          <div className="flex gap-3 mt-6">
            <button
              type="submit"
              className="primary-btn flex items-center gap-2"
              data-testid="lookup-submit-btn"
            >
              <Search size={18} />
              View History
            </button>
            {(phone || name) && (
              <button
                type="button"
                className="secondary-btn"
                onClick={() => { setPhone(''); setName(''); setActiveField(null); }}
                data-testid="lookup-clear-btn"
              >
                Clear
              </button>
            )}
          </div>
        </form>
      </div>

      {!loading && patients.length > 0 && !phone && !name && (
        <div className="card p-6" data-testid="lookup-recent">
          <h2 className="text-lg font-bold mb-4" style={{ color: '#2D3A2A', fontFamily: 'Outfit, sans-serif' }}>
            Recently added patients
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[...patients]
              .filter((p) => !p.archived)
              .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
              .slice(0, 6)
              .map((p) => (
                <button
                  key={p.id}
                  onClick={() => goToHistory(p)}
                  className="flex items-center gap-3 p-3 rounded-lg text-left hover:bg-gray-50 transition-colors"
                  style={{ border: '1px solid #E5E0D8' }}
                  data-testid={`lookup-recent-${p.id}`}
                >
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F5E6F5' }}>
                    <User size={20} style={{ color: '#990099' }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate" style={{ color: '#2D3A2A' }}>
                      {p.name || <span style={{ color: '#9CA3AF', fontStyle: 'italic' }}>No name</span>}
                    </p>
                    <p className="text-xs truncate" style={{ color: '#6B7280' }}>{p.phone}</p>
                  </div>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PatientLookup;
